import React, { useState, useMemo, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Tag, Plus, CheckIcon, ChevronLeft, X } from "lucide-react";
import { LabelInterface, ColorItem } from "@/types/labels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { toast } from "sonner";
import { COLOR_LIST } from "@/utils/constants";
import { cn } from "@/lib/utils";
import { Issue } from "@/types/issues";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useActiveProject } from "@/utils/utils";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { getAppliedStatusIconForIssues } from "@/utils/utils";
import {
  createLabelWithRetrySingle,
  sanitizeLabelName,
  validateLabelName,
  getColorValue,
} from "@/utils/labelUtils";

interface LabelBadgeProps {
  className?: string;
  issue?: Issue;
  projectLabels?: LabelInterface[];
  labelId?: string;
  labelsToStack?: LabelInterface[];
  labelIdsFallback?: string[] | null;
  labelIdsToStackFallback?: string[] | null;
  onChange?: (newLabels: string[]) => void;
  iconColor?: string;
  showPlusButton?: boolean;
  title?: string;
  iconOnly?: boolean;
  iconSize?: number;
}

export const LabelBadge: React.FC<LabelBadgeProps> = ({
  className = "",
  issue,
  projectLabels = [],
  labelId = "",
  labelsToStack = [],
  labelIdsFallback = null,
  labelIdsToStackFallback = null,
  onChange,
  iconColor = "var(--text-primary)",
  showPlusButton = false,
  iconOnly = false,
  title = "Labels",
  iconSize = 16,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newLabelName, setNewLabelName] = useState("");
  const [labelView, setLabelView] = useState<"list" | "new" | "colorSelect">(
    "list",
  );
  const project = useActiveProject();
  const updateIssueLabels = useIssuesStore((state) => state.updateIssueLabels);
  const createIssueLabel = useIssuesStore((state) => state.createIssueLabel);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const labelInputRef = React.useRef<HTMLInputElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { selectedIssueIds } = useIssueSelectionStore();
  if (!project || !projectLabels) {
    projectLabels = [];
  }
  const label = projectLabels.find((l) => l.id === labelId);

  const currentLabels = (issue?.canvas.labels ?? labelIdsFallback ?? []).filter(
    (l) => l !== null,
  ) as unknown as string[];

  let currentLabelsToStack: LabelInterface[] = labelsToStack.filter(
    (l) => l !== null,
  );
  if (labelIdsToStackFallback) {
    currentLabelsToStack = [
      ...labelIdsToStackFallback.map(
        (l) => projectLabels.find((pl) => pl.id === l) as LabelInterface,
      ),
    ];
  }

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  // Add focus effect when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Use a small timeout to ensure the dropdown is rendered
      const timeoutId = setTimeout(() => {
        if (labelView === "list") {
          searchInputRef.current?.focus();
        } else if (labelView === "new") {
          labelInputRef.current?.focus();
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, labelView]);

  useEffect(() => {
    return () => {
      setLabelView("list");
      setNewLabelName("");
      setSearchQuery("");
    };
  }, []);

  // Add scrollbar styles
  const scrollbarStyles = `
    .context-menu-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .context-menu-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .context-menu-scroll::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.3);
      border-radius: 4px;
    }
    .context-menu-scroll::-webkit-scrollbar-thumb:hover {
      background-color: rgba(255, 255, 255, 0.5);
    }
  `;

  const sortedAndFilteredLabels = useMemo(() => {
    const filtered = projectLabels.filter((label) =>
      label.label?.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    // Split into applied and unapplied labels
    const applied = filtered
      .filter((label) => currentLabels.some((l) => l === label.id))
      .sort((a, b) =>
        a.label?.toLowerCase().localeCompare(b.label?.toLowerCase() ?? ""),
      );

    const available = filtered
      .filter((label) => !currentLabels.some((l) => l === label.id))
      .sort((a, b) =>
        a.label?.toLowerCase().localeCompare(b.label?.toLowerCase() ?? ""),
      );

    return { applied, available };
  }, [projectLabels, searchQuery, currentLabels]);

  const shouldShowCreateButton = useMemo(() => {
    return (
      searchQuery.trim() &&
      sortedAndFilteredLabels.applied.length === 0 &&
      sortedAndFilteredLabels.available.length === 0
    );
  }, [searchQuery, sortedAndFilteredLabels]);

  const shouldShowHelperText = useMemo(() => {
    return projectLabels.length === 0 && !searchQuery.trim();
  }, [projectLabels.length, searchQuery]);

  const getLabelIcon = (labelId: string) => {
    const selectedIssuesArray = Array.from(selectedIssueIds);
    if (selectedIssuesArray.length > 1) {
      return getAppliedStatusIconForIssues(
        selectedIssuesArray,
        labelId,
        (issue, labelId) =>
          Array.isArray(issue.canvas.labels) &&
          issue.canvas.labels.includes(labelId ?? ""),
      );
    }

    const hasLabel = currentLabels.some((l) => l === labelId);
    if (hasLabel) {
      return <CheckIcon className="size-3.5" color="#F5F5F6" />;
    }
    return null;
  };

  const handleLabelToggle = async (labelId: string) => {
    const selectedLabel = projectLabels.find((l) => l.id === labelId);
    if (!selectedLabel) return;

    try {
      const hasLabel = currentLabels.some((l) => l === labelId);
      if (hasLabel) {
        const updatedLabelIds = currentLabels.filter((l) => l !== labelId);
        const updatedLabelInterfaces = updatedLabelIds.map((l) =>
          projectLabels.find((pl) => pl.id === l),
        );

        if (!issue && onChange) {
          onChange(updatedLabelIds);
          return;
        }

        updateIssueLabels(
          supabaseClient,
          issue?.canvas.id ?? "",
          (updatedLabelInterfaces ?? []) as LabelInterface[],
        );
        toast.success(`Removed label: ${selectedLabel.label}`);
      } else {
        const updatedLabelIds = [...currentLabels, labelId];
        const updatedLabelInterfaces = updatedLabelIds.map((l) =>
          projectLabels.find((pl) => pl.id === l),
        );

        if (!issue && onChange) {
          onChange(updatedLabelIds);
          return;
        }

        updateIssueLabels(
          supabaseClient,
          issue?.canvas.id ?? "",
          (updatedLabelInterfaces ?? []) as LabelInterface[],
        );
        toast.success(`Added label: ${selectedLabel.label}`);
      }
    } catch (error) {
      console.error("Failed to update label:", error);
      toast.error("Failed to update label");
    }
  };

  const handleCreateNewLabel = () => {
    const sanitizedQuery = sanitizeLabelName(searchQuery);
    if (validateLabelName(sanitizedQuery)) {
      setNewLabelName(sanitizedQuery);
      setLabelView("colorSelect");
    } else {
      toast.error("Please enter a valid label name (1-50 characters)");
    }
  };

  const handleColorSelect = async (color: ColorItem) => {
    const projectId = issue?.canvas.project_id || project?.id;

    if (!supabaseClient || !projectId || !project) {
      toast.error("Unable to create label. Please try again.");
      return;
    }

    // Create a new label with the selected color
    const newLabel: LabelInterface = {
      id: crypto.randomUUID(),
      name: newLabelName,
      color: { value: color.value },
      label: newLabelName, // For backward compatibility
    };

    try {
      // Add the label to the project
      await createIssueLabel(supabaseClient, project, newLabel);

      if (issue) {
        // For existing issues, update the issue labels
        await createLabelWithRetrySingle(
          newLabel,
          projectId,
          supabaseClient,
          updateIssueLabels,
          issue.canvas.id ?? "",
          currentLabels,
          projectLabels,
        );
      } else if (onChange) {
        // For new canvas creation, update the form state
        const updatedLabels = [...currentLabels, newLabel.id];
        onChange(updatedLabels);
      }

      setIsOpen(false);
      setLabelView("list");
      setNewLabelName("");
      setSearchQuery("");
    } catch (error) {
      console.error("Label creation failed:", error);
      toast.error("Failed to create label. Please try again.");
    }
  };

  const BadgeContent = ({
    showName = true,
    badgeLabel = null,
    index = 0,
  }: {
    showName?: boolean;
    badgeLabel?: LabelInterface | null;
    index?: number;
  }) => (
    <>
      {badgeLabel ? (
        <>
          <div
            className={cn(
              "shrink-0 wrap-break-word",
              index !== 0 && !showName
                ? "w-3 h-3 -ml-2.5 border border-secondary-n-900 rounded-[4px]"
                : "size-2.5 rounded-[3px]",
            )}
            style={{
              backgroundColor: getColorValue(
                projectLabels.find((l) => l.id === badgeLabel.id)?.color,
              ),
            }}
          />
          {showName && (
            <span className="truncate max-w-[250px]">
              {badgeLabel.label || badgeLabel.name}
            </span>
          )}
        </>
      ) : (
        <>
          <Tag
            className="text-icons-secondary"
            color={iconColor}
            size={iconSize}
          />
          <span className="text-xs font-medium text-text-secondary">
            {!iconOnly && title}
          </span>
        </>
      )}
    </>
  );

  const StackedBadgeContent = () => {
    const shouldShowName = currentLabelsToStack.length === 1;

    return (
      <>
        {showPlusButton ? (
          <div className="flex items-center justify-center w-full h-full">
            <Plus className="w-4 h-4 text-text-secondary hover:text-text-primary" />
          </div>
        ) : (
          <>
            {currentLabelsToStack
              .filter((label) => Boolean(label))
              .slice(0, 3)
              .map((stackingLabel, index) => (
                <BadgeContent
                  key={stackingLabel.id}
                  showName={shouldShowName}
                  badgeLabel={stackingLabel}
                  index={index}
                />
              ))}

            {currentLabelsToStack.length > 1
              ? `${currentLabelsToStack.length} Labels`
              : ""}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <div>
            <Badge
              variant="outline"
              className={cn(
                "h-6 py-0 px-2 text-xs font-normal flex items-center gap-1.5 cursor-pointer",
                "hover:bg-secondary-n-800 bg-secondary-n-950",
                className,
              )}
            >
              {currentLabelsToStack.length > 0 ? (
                <StackedBadgeContent />
              ) : (
                <BadgeContent showName={true} badgeLabel={label} />
              )}
            </Badge>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          ref={menuRef}
          className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg p-0"
          onClick={(e) => e.stopPropagation()}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            setLabelView("list");
            setNewLabelName("");
            setSearchQuery("");
          }}
          onPointerDownOutside={(e) => {
            if (!menuRef.current?.contains(e.target as Node)) {
              setIsOpen(false);
            }
          }}
          onInteractOutside={(e) => {
            if (searchInputRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          {labelView === "list" && (
            <>
              <div className="p-2">
                <div className="relative">
                  <Input
                    ref={searchInputRef}
                    placeholder="Search or add labels"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter" && shouldShowCreateButton) {
                        e.preventDefault();
                        handleCreateNewLabel();
                      }
                    }}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      // Only prevent default if we're still in the dropdown
                      if (
                        isOpen &&
                        menuRef.current?.contains(e.relatedTarget as Node)
                      ) {
                        e.preventDefault();
                        searchInputRef.current?.focus();
                      }
                    }}
                    className="h-7 text-sm bg-secondary-n-950 border-0! border-none! focus:border-0! focus:border-none! focus-visible:border-0! focus-visible:border-none! focus-visible:ring-0! focus-visible:!ring-none placeholder:text-secondary-n-600 pr-6"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              </div>
              <DropdownMenuSeparator className="m-0" />
              <div
                className="overflow-y-auto overflow-x-hidden context-menu-scroll max-h-[calc(70vh-200px)]"
                style={{
                  scrollbarWidth: "none",
                }}
              >
                {sortedAndFilteredLabels.applied.length > 0 && (
                  <div className="p-1">
                    {sortedAndFilteredLabels.applied.map((l) => (
                      <DropdownMenuItem
                        key={l.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLabelToggle(l.id);
                        }}
                        className="flex items-center hover:bg-secondary-n-950"
                      >
                        <div className="flex items-center w-full py-1.5 px-2 min-w-0">
                          <div className="flex items-center mr-1 gap-1 shrink-0">
                            <div className="size-5 rounded-sm p-[2px] flex items-center justify-center bg-secondary-n-700 border border-secondary-n-700">
                              {getLabelIcon(l.id)}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 ml-3 min-w-0">
                            <div
                              className="size-4 rounded-[3px] shrink-0"
                              style={{
                                backgroundColor: getColorValue(l.color),
                              }}
                            />
                            <span className="truncate">
                              {l.label || l.name}
                            </span>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )}

                {sortedAndFilteredLabels.available.length > 0 && (
                  <>
                    {sortedAndFilteredLabels.applied.length > 0 && (
                      <>
                        <DropdownMenuSeparator className="m-0" />
                        <div className="px-4">
                          <DropdownMenuLabel className="pb-0 px-0 caption text-2xs font-medium text-secondary-n-400">
                            Labels
                          </DropdownMenuLabel>
                        </div>
                      </>
                    )}
                    {sortedAndFilteredLabels.available.map((l) => (
                      <div className="px-1 py-1">
                        <DropdownMenuItem
                          key={l.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLabelToggle(l.id);
                          }}
                          className="flex items-center hover:bg-secondary-n-950 group"
                        >
                          <div className="flex items-center w-full py-1.5 px-2 min-w-0">
                            <div className="flex items-center gap-1 shrink-0">
                              <div className="p-[2px] w-5 h-5 rounded-sm flex items-center justify-center bg-transparent border border-transparent group-hover:border-secondary-n-700 group-hover:bg-secondary-n-950 transition-colors" />
                            </div>
                            <div className="flex items-center gap-3 ml-3 min-w-0">
                              <div
                                className="size-4 rounded-[3px] shrink-0"
                                style={{
                                  backgroundColor: getColorValue(l.color),
                                }}
                              />
                              <span className="truncate">
                                {l.label || l.name}
                              </span>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      </div>
                    ))}
                  </>
                )}

                {shouldShowHelperText && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground text-center">
                    Start typing to create a new label
                  </div>
                )}

                {shouldShowCreateButton && (
                  <div className="p-1">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCreateNewLabel();
                      }}
                      className="flex items-center justify-center gap-2 text-sm cursor-pointer bg-background-CTA hover:brightness-110! text-white m-1 mt-2 rounded-md"
                    >
                      <Plus className="size-4" />
                      Create new label
                    </DropdownMenuItem>
                  </div>
                )}
              </div>
            </>
          )}

          {labelView === "new" && (
            <div className="p-1">
              <div className="px-2 py-1.5">
                <Input
                  ref={labelInputRef}
                  placeholder="Label name..."
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter" && newLabelName.trim()) {
                      e.preventDefault();
                      setLabelView("colorSelect");
                    }
                  }}
                  onKeyUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    // Only prevent default if we're still in the dropdown
                    if (
                      isOpen &&
                      menuRef.current?.contains(e.relatedTarget as Node)
                    ) {
                      e.preventDefault();
                      labelInputRef.current?.focus();
                    }
                  }}
                  className="h-7 text-sm bg-secondary-n-950 border-0"
                />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!newLabelName?.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLabelView("colorSelect");
                }}
                className="flex justify-start gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="size-4" />
                <span className="overflow-hidden wrap-break-word">
                  Add new label: {newLabelName}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex justify-between gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLabelView("list");
                  setNewLabelName("");
                }}
              >
                Back
                <ChevronLeft className="size-4" />
              </DropdownMenuItem>
            </div>
          )}

          {labelView === "colorSelect" && (
            <div className="p-1">
              <DropdownMenuItem
                disabled
                className="overflow-hidden wrap-break-word"
              >
                Pick a color for label
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {COLOR_LIST.map((color) => (
                <DropdownMenuItem
                  key={color.key}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleColorSelect(color);
                  }}
                  className="flex gap-2 items-center text-sm cursor-pointer"
                >
                  <div
                    className="size-2.5 rounded-[3px] shrink-0"
                    style={{ backgroundColor: color.value }}
                  />
                  <span>{color.label}</span>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
